import { useMemo } from "react";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { GroupedCards } from "../types";

interface UseUnifiedItemsGroupingProps {
  cards: CardData[];
  folders: any[];
  selectedFolderId: string | null;
}

interface UseUnifiedItemsGroupingResult {
  groupedCards: GroupedCards[];
}

/**
 * Hook for grouping cards by folder
 * This is generic logic that works the same for all item types
 */
export function useUnifiedItemsGrouping({
  cards,
  folders,
  selectedFolderId,
}: UseUnifiedItemsGroupingProps): UseUnifiedItemsGroupingResult {
  const groupedCards = useMemo(() => {
    // Helper function to find folder by id (recursive)
    const findFolder = (folders: any[], folderId: string): any => {
      for (const folder of folders) {
        if (folder.id === folderId) return folder;
        if (folder.subfolder && folder.subfolder.length > 0) {
          const found = findFolder(folder.subfolder, folderId);
          if (found) return found;
        }
      }
      return null;
    };

    // Helper function to get the top-level folder for any folder ID
    const getTopLevelFolder = (folderId: string, folders: any[]): any => {
      // Check if this folder is already at top level
      const isTopLevel = folders.some(f => f.id === folderId);
      if (isTopLevel) {
        return folders.find(f => f.id === folderId);
      }

      // Otherwise, search recursively for the parent that is at top level
      for (const topFolder of folders) {
        const found = findFolder(topFolder.subfolder || [], folderId);
        if (found) {
          return topFolder; // Return the top-level folder
        }
      }
      return null;
    };

    // At root level, group by TOP-LEVEL folders only
    if (selectedFolderId === null) {
      const groups: GroupedCards[] = [];
      const folderMap = new Map<string | null, CardData[]>();

      // Group cards by their top-level folder
      cards.forEach(card => {
        const cardFolderId = card.folder;
        if (!cardFolderId) {
          // Unfiled items
          if (!folderMap.has(null)) {
            folderMap.set(null, []);
          }
          folderMap.get(null)!.push(card);
        } else {
          // Find the top-level folder for this card
          const topLevelFolder = getTopLevelFolder(cardFolderId as string, folders);
          if (topLevelFolder) {
            if (!folderMap.has(topLevelFolder.id)) {
              folderMap.set(topLevelFolder.id, []);
            }
            
            // Add subfolder name and color to card if it's not directly in top-level folder
            const cardFolder = findFolder(folders, cardFolderId as string);
            const isDirectChild = topLevelFolder.id === cardFolderId;
            const enrichedCard = {
              ...card,
              subfolderName: !isDirectChild && cardFolder ? cardFolder.name : undefined,
              subfolderColor: !isDirectChild && cardFolder ? cardFolder.color : undefined,
              folderColor: topLevelFolder.color, // Always add the top-level folder color
            };
            
            folderMap.get(topLevelFolder.id)!.push(enrichedCard);
          }
        }
      });

      // Convert to array with folder names
      folderMap.forEach((cards, folderId) => {
        let folderName = "Unfiled Items";
        if (folderId) {
          const folder = folders.find(f => f.id === folderId);
          folderName = folder ? folder.name : `Folder ${folderId.substring(0, 8)}`;
        }
        groups.push({ folderId, folderName, cards });
      });

      // Sort groups: unfiled first, then alphabetically by folder name
      groups.sort((a, b) => {
        if (a.folderId === null) return -1;
        if (b.folderId === null) return 1;
        return a.folderName.localeCompare(b.folderName);
      });

      return groups;
    }

    // When inside a folder, group by the actual folder each card belongs to
    const groups: GroupedCards[] = [];
    const folderMap = new Map<string | null, CardData[]>();

    // Group cards by their actual folder
    cards.forEach(card => {
      const cardFolderId = card.folder || null;
      
      if (!folderMap.has(cardFolderId as string)) {
        folderMap.set(cardFolderId as string, []);
      }
      
      // Add folder color to card
      const cardFolder = cardFolderId ? findFolder(folders, cardFolderId as string) : null;
      const enrichedCard = {
        ...card,
        folderColor: cardFolder?.color,
      };
      
      folderMap.get(cardFolderId as string)!.push(enrichedCard);
    });

    // Convert to array with folder names
    folderMap.forEach((cards, folderId) => {
      let folderName = "Items";
      if (folderId) {
        const folder = findFolder(folders, folderId);
        folderName = folder ? folder.name : `Folder ${folderId.substring(0, 8)}`;
      }
      groups.push({ folderId, folderName, cards });
    });

    // Sort groups: unfiled first, then alphabetically by folder name
    groups.sort((a, b) => {
      if (a.folderId === null) return -1;
      if (b.folderId === null) return 1;
      return a.folderName.localeCompare(b.folderName);
    });

    return groups;
  }, [cards, folders, selectedFolderId]);

  return { groupedCards };
}
